import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { APIGetAssetComplianceHistory } from '../../../config/restAPI/FetchAPI';
import {
  formatDate,
  formatDateTime,
  getMomentTimestamp,
  hasValue,
  parseApiListResponse,
  pickField,
  isApiResponseNotFound,
} from './maintenanceComplianceUtils';

const HISTORY_SECTIONS = [
  { id: 'all', label: 'All' },
  { id: 'roadTax', label: 'Road Tax' },
  { id: 'puspakom', label: 'PUSPAKOM Inspection' },
  { id: 'insurance', label: 'Insurance' },
];

const SECTION_LABELS = {
  roadTax: 'Road Tax',
  puspakom: 'PUSPAKOM Inspection',
  insurance: 'Insurance',
};

const getRecordSection = (record) => pickField(record, 'section', 'type') || 'unknown';

const getRecordData = (record) => record?.data || record?.payload || {};

const getRecordArchivedAt = (record) => pickField(
  record,
  'archivedAt',
  'archived_at',
  'createdAt',
  'created_at',
);

const formatHistorySummary = (section, data) => {
  if (section === 'roadTax') {
    const expiry = pickField(data, 'roadTaxExpiry', 'road_tax_expiry');
    return hasValue(expiry) ? `Expired ${formatDate(expiry)}` : '-';
  }
  if (section === 'puspakom') {
    const nextDue = pickField(data, 'nextDue', 'next_due');
    const result = pickField(data, 'result');
    const parts = [];
    if (hasValue(nextDue)) parts.push(`Due ${formatDate(nextDue)}`);
    if (hasValue(result)) parts.push(result);
    return parts.length > 0 ? parts.join(' · ') : '-';
  }
  if (section === 'insurance') {
    const provider = pickField(data, 'provider');
    const expiry = pickField(data, 'expiry');
    const parts = [];
    if (hasValue(provider)) parts.push(provider);
    if (hasValue(expiry)) parts.push(`Expires ${formatDate(expiry)}`);
    return parts.length > 0 ? parts.join(' · ') : '-';
  }
  return '-';
};

const formatHistoryDetails = (section, data) => {
  if (section === 'roadTax') {
    return [{ label: 'Expiry date', value: formatDate(pickField(data, 'roadTaxExpiry', 'road_tax_expiry')) }];
  }
  if (section === 'puspakom') {
    return [
      { label: 'Last inspection date', value: formatDate(pickField(data, 'lastDate', 'last_date')) },
      { label: 'Next due date', value: formatDate(pickField(data, 'nextDue', 'next_due')) },
      { label: 'Result', value: pickField(data, 'result') || '-' },
      { label: 'Certificate number', value: pickField(data, 'certNumber', 'cert_number') || '-' },
      { label: 'Inspection centre', value: pickField(data, 'centre', 'center') || '-' },
    ];
  }
  if (section === 'insurance') {
    return [
      { label: 'Provider', value: pickField(data, 'provider') || '-' },
      { label: 'Policy number', value: pickField(data, 'policyNumber', 'policy_number') || '-' },
      { label: 'Expiry date', value: formatDate(pickField(data, 'expiry')) },
    ];
  }
  return [];
};

const MaintenanceComplianceHistoryTab = ({
  selectedAsset,
  token,
  agencyID,
}) => {
  const [historyRecords, setHistoryRecords] = useState([]);
  const [activeSection, setActiveSection] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const assetId = selectedAsset?.id;
  const assetIdRef = useRef(assetId);

  useEffect(() => {
    assetIdRef.current = assetId;
  }, [assetId]);

  useEffect(() => {
    setHistoryRecords([]);
    setExpandedId(null);
    setError('');
    setLoading(Boolean(assetId));
  }, [assetId]);

  useEffect(() => {
    setExpandedId(null);
  }, [activeSection]);

  const fetchHistory = useCallback(async () => {
    if (!assetId || !token || !agencyID) return;
    const requestAssetId = assetId;
    setError('');
    setLoading(true);
    try {
      const sectionParam = activeSection === 'all' ? undefined : activeSection;
      const result = await APIGetAssetComplianceHistory(
        () => {},
        token,
        agencyID,
        assetId,
        sectionParam,
      );
      if (requestAssetId !== assetIdRef.current) return;
      if (isApiResponseNotFound(result)) {
        setHistoryRecords([]);
        return;
      }
      const data = parseApiListResponse(result);
      if (requestAssetId !== assetIdRef.current) return;
      if (data) {
        const sorted = [...data].sort((a, b) => (
          getMomentTimestamp(getRecordArchivedAt(b)) - getMomentTimestamp(getRecordArchivedAt(a))
        ));
        setHistoryRecords(sorted);
      } else {
        setError('Unable to load compliance history.');
      }
    } catch {
      if (requestAssetId === assetIdRef.current) {
        setError('Unable to load compliance history.');
      }
    } finally {
      if (requestAssetId === assetIdRef.current) {
        setLoading(false);
      }
    }
  }, [assetId, token, agencyID, activeSection]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const filteredRecords = activeSection === 'all'
    ? historyRecords
    : historyRecords.filter((record) => getRecordSection(record) === activeSection);

  return (
    <div className="maintenance-compliance-history-tab">
      <div className="maintenance-detail-card">
        <div className="maintenance-detail-row">
          <span className="maintenance-detail-label">Asset name</span>
          <span className="maintenance-detail-value">
            {selectedAsset.assetName || selectedAsset.deviceSN}
          </span>
        </div>
      </div>

      <div className="maintenance-compliance-section">
        <div className="maintenance-compliance-section-header">
          <h3 className="maintenance-compliance-section-title">Compliance History</h3>
        </div>

        <div className="maintenance-compliance-history-filters">
          {HISTORY_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`maintenance-compliance-history-filter${
                activeSection === section.id ? ' maintenance-compliance-history-filter--active' : ''
              }`}
              aria-pressed={activeSection === section.id}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div className="maintenance-compliance-section-body">
          <p className="maintenance-compliance-history-desc">
            Previous road tax, PUSPAKOM inspection, and insurance records archived when renewed.
          </p>

          {error && (
            <div className="maintenance-compliance-inline-error">{error}</div>
          )}

          {loading ? (
            <div className="maintenance-compliance-loading">Loading compliance history…</div>
          ) : filteredRecords.length === 0 ? (
            <div className="maintenance-compliance-history-empty">
              No archived compliance records yet.
            </div>
          ) : (
            <div className="maintenance-compliance-history-list">
              {filteredRecords.map((record, index) => {
                const section = getRecordSection(record);
                const data = getRecordData(record);
                const recordId = pickField(record, 'id', '_id') || `${section}-${index}`;
                const isExpanded = expandedId === recordId;
                const details = formatHistoryDetails(section, data);

                return (
                  <div key={recordId} className="maintenance-compliance-history-item">
                    <button
                      type="button"
                      className="maintenance-compliance-history-item-header"
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedId(isExpanded ? null : recordId)}
                    >
                      <div className="maintenance-compliance-history-item-main">
                        <span className="maintenance-compliance-history-item-type">
                          {SECTION_LABELS[section] || section}
                        </span>
                        <span className="maintenance-compliance-history-item-summary">
                          {formatHistorySummary(section, data)}
                        </span>
                      </div>
                      <span className="maintenance-compliance-history-item-date">
                        Archived {formatDateTime(getRecordArchivedAt(record))}
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="maintenance-compliance-history-item-details">
                        {details.map((row) => (
                          <div key={row.label} className="maintenance-compliance-history-detail-row">
                            <span className="maintenance-compliance-history-detail-label">{row.label}</span>
                            <span className="maintenance-compliance-history-detail-value">{row.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MaintenanceComplianceHistoryTab.propTypes = {
  selectedAsset: PropTypes.shape({
    id: PropTypes.string,
    assetName: PropTypes.string,
    deviceSN: PropTypes.string,
  }).isRequired,
  token: PropTypes.string,
  agencyID: PropTypes.string,
};

MaintenanceComplianceHistoryTab.defaultProps = {
  token: '',
  agencyID: '',
};

export default MaintenanceComplianceHistoryTab;
